import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Home, 
  CheckSquare, 
  ShoppingBag, 
  Settings, 
  LogOut,
  Heart,
  Package
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { 
      path: '/dashboard', 
      icon: Home, 
      label: 'Home',
      color: 'text-pookie-pink-600'
    },
    { 
      path: '/tasks', 
      icon: CheckSquare, 
      label: 'Tasks',
      color: 'text-pookie-purple-600'
    },
    { 
      path: '/store', 
      icon: ShoppingBag, 
      label: 'Store',
      color: 'text-pookie-pink-600'
    },
    ...(user?.role === 'admin' ? [{ 
      path: '/admin', 
      icon: Settings, 
      label: 'Admin',
      color: 'text-pookie-blue-600'
    }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pookie-pink-50 to-pookie-purple-50">
      {/* Mobile Header */}
      <header className="lg:hidden bg-white/90 backdrop-blur-sm border-b border-pookie-pink-100 px-4 py-3 safe-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Heart className="w-8 h-8 text-pookie-pink-500" />
            <div>
              <h1 className="text-xl font-bold text-gradient text-cute">PookieMart</h1>
              <p className="text-xs text-gray-600">Hi, {user?.displayName}! 💕</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="points-display text-xs">
              💎 {user?.pookiePoints || 0}
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex">
        <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white/90 backdrop-blur-sm border-r border-pookie-pink-100 shadow-lg">
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b border-pookie-pink-100">
              <div className="flex items-center space-x-3">
                <Heart className="w-10 h-10 text-pookie-pink-500" />
                <div>
                  <h1 className="text-2xl font-bold text-gradient text-cute">PookieMart</h1>
                  <p className="text-sm text-gray-600">Made with love 💕</p>
                </div>
              </div>
            </div>

            {/* User Info */}
            <div className="p-6 border-b border-pookie-pink-100">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-pookie-pink-400 to-pookie-purple-400 rounded-full flex items-center justify-center text-white text-xl">
                  {user?.avatar || user?.displayName?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{user?.displayName}</p>
                  <div className="points-display text-xs mt-1">
                    💎 {user?.pookiePoints || 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map(({ path, icon: Icon, label, color }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 hover:scale-105 ${
                    isActive(path)
                      ? 'bg-pookie-pink-100 text-pookie-pink-600 shadow-md'
                      : 'text-gray-600 hover:bg-pookie-pink-50'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive(path) ? color : 'text-gray-400'}`} />
                  <span className="font-medium">{label}</span>
                </Link>
              ))}
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t border-pookie-pink-100">
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200 hover:scale-105"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:ml-64">
        <div className="container-responsive py-mobile min-h-screen">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden nav-mobile">
        <div className="flex justify-around items-center">
          {navItems.map(({ path, icon: Icon, label, color }) => (
            <Link
              key={path}
              to={path}
              className={`nav-item ${isActive(path) ? 'active' : ''}`}
            >
              <Icon className={`w-5 h-5 ${isActive(path) ? color : 'text-gray-400'}`} />
              <span className="text-2xs font-medium">{label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Cute floating heart animation */}
      <div className="fixed bottom-20 right-4 lg:bottom-4 lg:right-6 pointer-events-none">
        <Heart className="w-6 h-6 text-pookie-pink-300 animate-pulse-heart opacity-50" />
      </div>
    </div>
  );
};

export default Layout; 